































































import { computed, defineComponent, PropType } from "@vue/composition-api";
import { ContentSet } from "@scrile/api-provider/dist/api/ContentSetProvider";
import { ContentFeed, ContentFeedTypeEnum } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import { parseISO } from "@scrile/tools/dist/lib/TimeHelpers";
import useCoverThumbnail from "@/hooks/useCoverThumbnail";
import { getFilesIcon, isMediaFile } from "@/lib/fileHelpers";
import { format, getNow } from "@/lib/time";
import config from "@/config";
import BaseChip from "@/components/BaseChip";
import BaseIcon from "@/components/BaseIcon";
import BaseImage from "@/components/BaseImage";
import ContentCover from "@/components/ContentCover";

function isContentFeed(f: ContentFeed | ContentSet): f is ContentFeed {
  return "files" in f;
}
export default defineComponent({
  name: "ContentFeedCover",
  components: { BaseChip, BaseIcon, ContentCover, BaseImage },
  props: {
    postData: { type: Object as PropType<ContentFeed | ContentSet>, required: true },
    small: { type: Boolean, default: false },
    hideIcon: { type: Boolean, default: false },
    authUserId: { type: String, default: "" },
    currentAuthorities: { type: Array as PropType<string[]>, default: () => [] },
    enablePinPost: { type: Boolean, default: true },
  },
  setup(props) {
    const coverFile = computed(() =>
      isContentFeed(props.postData)
        ? props.postData.type === ContentFeedTypeEnum.RICH_TEXT
          ? props.postData.files.find(f => f.publicAccess)
          : props.postData.files.find(f => isMediaFile(f))
        : props.postData.cover
    );

    const { coverSrc } = useCoverThumbnail(coverFile);

    const isAuthor = computed(() => props.authUserId === props.postData.userId);
    const showErrorIcon = computed(() => props.postData.processingStatus === "ERROR");
    const showProcessingIcon = computed(() => ["NEW", "CONVERTING"].indexOf(props.postData.processingStatus) > -1);
    const showScheduledIcon = computed(
      () => props.postData.scheduleTime && parseISO(props.postData.scheduleTime).getTime() >= getNow().getTime()
    );
    const scheduledText = computed(() =>
      props.postData.scheduleTime ? format(parseISO(props.postData.scheduleTime), "MM.dd.yy") : ""
    );
    const isNotPublished = computed(() => !props.postData.publishTime);
    const allowPinPosts = computed(() => config.featurePinPost);

    const showGradient = computed(
      () =>
        isAuthor.value &&
        (showErrorIcon.value || showProcessingIcon.value || showScheduledIcon.value || isNotPublished.value)
    );

    const title = computed(() => props.postData.title);
    const showDarkGradient = computed(() => props.postData.type === ContentFeedTypeEnum.RICH_TEXT && !!coverFile.value);

    const icon = computed(() => {
      if (props.postData.type === ContentFeedTypeEnum.EVENT) {
        return "sci-calendar";
      } else if (props.postData.type === ContentFeedTypeEnum.STORY) {
        return "sci-story";
      } else if (props.postData.type === ContentFeedTypeEnum.RICH_TEXT) {
        return "sci-text-post";
      } else if (props.postData.type === ContentFeedTypeEnum.MEDIA && isContentFeed(props.postData)) {
        return getFilesIcon(props.postData.files);
      } else {
        return "sci-file-multiple";
      }
    });

    const mainIcon = computed(() => {
      if (showProcessingIcon.value) return "sci-processing";
      if (showScheduledIcon.value) return "sci-watch";
      return "sci-eye-off";
    });

    const isPinned = computed(() => allowPinPosts.value && isContentFeed(props.postData) && !!props.postData.pinTime);
    const showLocked = computed(() =>
      isContentFeed(props.postData) &&
      checkAuthorities(props.currentAuthorities, isAuthor ? ["updateMyContentSet"] : ["updateAnyContentSet"])
        ? props.postData.subscriptionLevelId !== "0" || props.postData.pricePackageId !== "0"
        : false
    );

    return {
      showErrorIcon,
      showProcessingIcon,
      showLocked,
      showGradient,
      showScheduledIcon,
      coverSrc,
      icon,
      isPinned,
      mainIcon,
      scheduledText,
      title,
      showDarkGradient,
    };
  },
});
